import React, { useState, useEffect, FormEvent, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Settings as SettingsIcon, Users, Crown, Trash2, Upload, Globe, X, Loader2, Building2 } from "lucide-react";
import { useOrganization } from "@/hooks/useOrganization";
import { updateOrganization, getOrganizationMembersWithUserDetails, removeOrganizationMember, uploadOrganizationLogo, deleteOrganizationLogo } from "@/services/organizationService";
import { OrganizationMember } from "@/types/organization";
import { CreateOrganizationDialog } from "@/components/organization/CreateOrganizationDialog";
import { CustomDomainSettingsCard } from "@/components/organization/CustomDomainSettingsCard";
import { InvitationManager } from "@/components/organization/InvitationManager";
import { DangerZone } from "@/components/organization/DangerZone";
import { toast } from "@/hooks/use-toast";
import { PageHeader } from '@/components/layout/PageHeader';

const OrganizationSettingsPage = () => {
  const { currentOrganization, isAdmin, isLoading, refreshOrganizations } = useOrganization();
  
  const [organizationName, setOrganizationName] = useState('');
  const [members, setMembers] = useState<(OrganizationMember & { displayName?: string; email?: string; photoURL?: string })[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [fetchDomain, setFetchDomain] = useState('');
  const [isFetchingLogo, setIsFetchingLogo] = useState(false);

  const loadMembers = useCallback(async () => {
    if (!currentOrganization) return;
    
    try {
      setLoadingMembers(true);
      const organizationMembers = await getOrganizationMembersWithUserDetails(currentOrganization.id);
      setMembers(organizationMembers);
    } catch (error) {
      console.error('Error loading members:', error);
      toast({
        title: "Error",
        description: "Failed to load organization members.",
        variant: "destructive"
      });
    } finally {
      setLoadingMembers(false);
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (currentOrganization) {
      setOrganizationName(currentOrganization.name);
      loadMembers();
    }
  }, [currentOrganization, loadMembers]);

  const handleRemoveMember = async (member: OrganizationMember & { displayName?: string; email?: string; photoURL?: string }) => {
    if (!currentOrganization || !isAdmin) return;
    
    const memberName = member.displayName || member.email || member.userId;
    const confirmMessage = `Are you sure you want to remove ${memberName} from the organization? This action cannot be undone.`;
    
    if (!confirm(confirmMessage)) return;
    
    try {
      await removeOrganizationMember(currentOrganization.id, member.userId);
      toast({
        title: "Member removed",
        description: `${memberName} has been removed from the organization.`,
      });
      // Reload members list
      await loadMembers();
    } catch (error) {
      console.error('Error removing member:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to remove member.",
        variant: "destructive"
      });
    }
  };

  const handleLogoUpload = async (file: File) => {
    if (!currentOrganization || !isAdmin) return;
    try {
      setIsUploadingLogo(true);
      await uploadOrganizationLogo(currentOrganization.id, file);
      await refreshOrganizations();
      toast({ title: "Logo updated", description: "Organization logo has been updated." });
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({ title: "Error", description: "Failed to upload logo.", variant: "destructive" });
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!currentOrganization || !isAdmin) return;
    try {
      setIsUploadingLogo(true);
      await deleteOrganizationLogo(currentOrganization.id);
      await refreshOrganizations();
      toast({ title: "Logo removed" });
    } catch (error) {
      console.error('Error removing logo:', error);
      toast({ title: "Error", description: "Failed to remove logo.", variant: "destructive" });
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleFetchLogo = async () => {
    if (!currentOrganization || !isAdmin || !fetchDomain.trim()) return;
    try {
      setIsFetchingLogo(true);
      const domain = fetchDomain.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
      const logoUrl = `https://img.logo.dev/${domain}?token=pk_a3-E5RCzS9CzxGENt9PrcQ&size=128&format=png`;
      const response = await fetch(logoUrl);
      if (!response.ok) throw new Error('Logo not found');
      const blob = await response.blob();
      const file = new File([blob], `${domain}-icon.png`, { type: 'image/png' });
      await handleLogoUpload(file);
      setFetchDomain('');
    } catch (error) {
      console.error('Error fetching logo:', error);
      toast({ title: "Error", description: "Could not fetch logo for this domain.", variant: "destructive" });
    } finally {
      setIsFetchingLogo(false);
    }
  };

  const handleSaveOrganization = async (event: FormEvent) => {
    event.preventDefault();
    if (!currentOrganization || !isAdmin) {
      toast({
        title: "Error",
        description: "You must be an admin to update organization settings.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSaving(true);
      await updateOrganization(currentOrganization.id, {
        name: organizationName.trim()
      });
      
      await refreshOrganizations();
      
      toast({
        title: "Success",
        description: "Organization settings updated successfully."
      });
    } catch (error) {
      console.error('Error updating organization:', error);
      toast({
        title: "Error",
        description: "Failed to update organization settings.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-col">
        <div className="px-4 sm:px-6 md:px-8 pt-2 pb-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Skeleton className="h-6 w-48" />
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0 px-4 sm:px-6 md:px-8 py-6 space-y-6">
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-72" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-24" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (!currentOrganization) {
    return (
      <div className="flex min-h-0 flex-col">
        <div className="flex-1 min-w-0 overflow-auto px-4 sm:px-6 md:px-8 py-6 flex flex-col items-center justify-center text-center space-y-4">
          <Building2 className="h-12 w-12 text-muted-foreground" />
          <div>
            <h2 className="text-xl font-semibold">No Organization Selected</h2>
            <p className="text-muted-foreground mb-4">
              Create an organization to collaborate with your team.
            </p>
            <CreateOrganizationDialog />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Organization Settings | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
        <PageHeader title="Settings" />

      {/* Scrollable Content */}
        <div className="flex-1 min-w-0 px-4 sm:px-6 md:px-8 py-6">
        <div className="space-y-6">
          <Tabs defaultValue="general">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-8">
              <TabsList className="grid w-fit grid-cols-3" layoutId="org-settings-tabs">
                <TabsTrigger value="general" className="text-sm">General</TabsTrigger>
                <TabsTrigger value="members" className="text-sm">Members</TabsTrigger>
                <TabsTrigger value="domain" className="text-sm">Domain</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="general" className="space-y-6">
              <div className="grid gap-6">
                {/* Organization Logo */}
                <Card>
                  <CardHeader>
                    <CardTitle>Logo</CardTitle>
                    <CardDescription>
                      Your organization's icon. Shown in the sidebar and across the platform.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-4">
                      {currentOrganization.logoUrl ? (
                        <img
                          src={currentOrganization.logoUrl}
                          alt={currentOrganization.name}
                          className="w-12 h-12 rounded-lg object-cover border border-border"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-foreground/10 flex items-center justify-center text-foreground text-lg font-semibold border border-border">
                          {currentOrganization.name?.charAt(0)?.toUpperCase() || 'O'}
                        </div>
                      )}
                      {isAdmin && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={isUploadingLogo}
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/png,image/jpeg,image/svg+xml,image/webp';
                              input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0];
                                if (file) handleLogoUpload(file);
                              };
                              input.click();
                            }}
                          >
                            {isUploadingLogo ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                            Upload
                          </Button>
                          {currentOrganization.logoUrl && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={isUploadingLogo}
                              onClick={handleLogoRemove}
                              className="text-muted-foreground"
                            >
                              <X className="h-3.5 w-3.5 mr-1.5" />
                              Remove
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    {isAdmin && (
                      <>
                        <Separator />
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Fetch icon from domain</Label>
                          <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                              <Globe className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                              <Input
                                value={fetchDomain}
                                onChange={(e) => setFetchDomain(e.target.value)}
                                placeholder="e.g. hihobbes.com"
                                className="pl-8 h-9"
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleFetchLogo(); } }}
                              />
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleFetchLogo}
                              disabled={isFetchingLogo || !fetchDomain.trim()}
                            >
                              {isFetchingLogo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Fetch'}
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Organization Details */}
                <Card>
                  <CardHeader>
                    <CardTitle>Organization Details</CardTitle>
                    <CardDescription>
                      Update your organization's basic information.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSaveOrganization} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="organizationName">Organization Name</Label>
                        <Input
                          id="organizationName"
                          type="text"
                          value={organizationName}
                          onChange={(e) => setOrganizationName(e.target.value)}
                          disabled={!isAdmin}
                          placeholder="Enter organization name"
                          required
                        />
                      </div>
                      
                      {isAdmin && (
                        <Button type="submit" disabled={isSaving}>
                          {isSaving ? "Saving..." : "Save Changes"}
                        </Button>
                      )}
                      
                      {!isAdmin && (
                        <p className="text-sm text-muted-foreground">
                          Only organization admins can update these settings.
                        </p>
                      )}
                    </form>
                  </CardContent>
                </Card>

                {/* Danger Zone - Admin Only */}
                <DangerZone />
              </div>
            </TabsContent>

            <TabsContent value="members" className="space-y-6">
              <div className="grid gap-6">
                {/* Organization Members */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Members ({members.length})
                    </CardTitle>
                    <CardDescription>
                      View and manage organization members.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingMembers ? (
                      <div className="space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <Skeleton className="h-10 w-10 rounded-full" />
                            <div className="flex-1">
                              <Skeleton className="h-4 w-32" />
                              <Skeleton className="h-3 w-20 mt-1" />
                            </div>
                            <Skeleton className="h-6 w-16" />
                          </div>
                        ))}
                      </div>
                    ) : members.length > 0 ? (
                      <div className="space-y-3">
                        {members.map((member) => (
                          <div key={member.id} className="flex flex-col gap-3 p-3 border rounded-lg sm:flex-row sm:items-center">
                            <Avatar className="h-10 w-10">
                              {member.photoURL && <AvatarImage src={member.photoURL} />}
                              <AvatarFallback>
                                {member.displayName != null
                                  ? member.displayName.slice(0, 2).toUpperCase()
                                  : member.email != null
                                  ? member.email.slice(0, 2).toUpperCase()
                                  : member.userId.slice(0, 2).toUpperCase()
                                }
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">
                                {member.displayName || member.email || member.userId}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Joined {member.joinedAt instanceof Date && !isNaN(member.joinedAt.getTime()) ? member.joinedAt.toLocaleDateString() : 'Recently'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 sm:ml-auto">
                              <Badge variant={member.role === 'admin' ? 'default' : 'secondary'}>
                                {member.role === 'admin' && <Crown className="h-3 w-3 mr-1" />}
                                {member.role}
                              </Badge>
                              {isAdmin && member.role !== 'admin' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveMember(member)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        No members found.
                      </div>
                    )}
                    
                  </CardContent>
                </Card>

                {/* Invitation Management */}
                <InvitationManager />
              </div>
              </TabsContent>

            <TabsContent value="domain" className="space-y-6">
              <div className="grid gap-6">
                <CustomDomainSettingsCard
                  organizationId={currentOrganization.id}
                  isAdmin={isAdmin}
                />
              </div>
            </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrganizationSettingsPage;
